/*
  File: src/auth/api.js
  Purpose: Centralized Axios client and API base URL selection for the app.
  Responsibilities:
  - Choose API base using localhost detection or REACT_APP_API_BASE.
  - Create a preconfigured Axios instance with JSON headers and credentials.
  - Intercept 401 responses to clear persisted auth state and let UI guards react.
  Key exports:
  - API: resolved base URL string
  - apiClient: configured Axios instance for all network calls
  Notes:
  - Auth relies on HttpOnly cookies; no Authorization header is set here.
*/
import axios from 'axios';

// Resolve API base URL
const isLocalhost = typeof window !== 'undefined' && (
  window.location.hostname === 'localhost' ||
  window.location.hostname === '127.0.0.1'
);
const rawBase = (process.env.REACT_APP_API_BASE || '').trim();
const cleanedBase = rawBase.replace(/^@+/, '');

// Always prefer explicit env var if provided; otherwise choose sensible default
export const API = cleanedBase || '/dev'
export const apiClient = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: {
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  }
});

// No Authorization header; we rely on HttpOnly cookie

// Add response interceptor to handle auth errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear auth data on 401
      localStorage.removeItem('auth:user');
      // Do not hard-redirect; let route guards handle navigation
    }
    return Promise.reject(error);
  }
);

// Detection management services
export const markLabelAsFalse = async (detectionId, falseLabel = null, trueLabel = null) => {
  try {
    const payload = {};
    if (falseLabel !== null) payload.false_label = falseLabel;
    if (trueLabel !== null) payload.true_label = trueLabel;

    const response = await apiClient.post(`/detections/${detectionId}/false`, payload);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to update detection');
  }
};

// Legacy function for backward compatibility (marks entire detection as false)
export const markDetectionAsFalse = async (detectionId, isFalseDetection = true) => {
  if (isFalseDetection) {
    // For marking as false, we need to provide at least an empty payload to indicate false detection
    return await markLabelAsFalse(detectionId, "", "");
  } else {
    // For clearing false detection, send empty payload
    return await markLabelAsFalse(detectionId, null, null);
  }
};


